import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { Icons } from "@/shared/assets/icon";

interface ImageUploadProps {
  onFileSelect: (files: FileList | null) => void;
  name?: string;
  accept?: string;
  multiple?: boolean;
  label?: string;
  subLabel?: string;
  maxFiles?: number;
  layout?: "row" | "column";
  browseButtonLabel?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  onFileSelect,
  accept = "image/*",
  name,
  multiple = false,
  label,
  subLabel,
  maxFiles = 5,
  layout = "column",
  browseButtonLabel,
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const defaultLabel =
    label || t("drag_and_drop_image_to_upload", { ns: "talent" });
  const defaultSubLabel =
    subLabel || t("svg_png_jpg_or_gif_max_800x400px", { ns: "talent" });
  const defaultBrowseLabel =
    browseButtonLabel || t("browse_image", { ns: "talent" });

  const handleFiles = (fileList: FileList | null) => {
    if (fileList) {
      const newFiles = Array.from(fileList);
      if (newFiles.length > maxFiles) {
        setError(
          t("maximum_files_allowed", { ns: "talent", count: maxFiles }).replace(
            "{count}",
            String(maxFiles),
          ),
        );
        return;
      }
      onFileSelect(fileList);
      setError(null);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  return (
    <Stack direction="row" gap={2}>
      <Box
        sx={{
          border: "2px dashed var(--primary-300)",
          backgroundColor: "var(--primary-100)",
          borderRadius: 3,
          p: layout === "row" ? 2 : 4,
          textAlign: "center",
          cursor: "pointer",
          flex: 1,
          "&:hover": {
            backgroundColor: "var(--primary-200)",
          },
          width: "100%",
          display: "flex",
          flexDirection: layout === "row" ? "row" : "column",
          alignItems: layout === "row" ? "center" : "stretch",
          justifyContent: layout === "row" ? "space-between" : "center",
          gap: layout === "row" ? 4 : 2,
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <Stack
          direction="column"
          gap={"var(--spacing-2x)"}
          sx={{
            flex: layout === "row" ? 1 : "none",
            alignItems: layout === "row" ? "flex-start" : "center",
            textAlign: layout === "row" ? "left" : "center",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              font: "var(--semibold-md)",
              color: "var(--primary-900)",
            }}
          >
            {defaultLabel}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              font: "var(--body-sm)",
              color: "var(--primary-800)",
            }}
          >
            {defaultSubLabel}
          </Typography>
        </Stack>
        <label htmlFor={name || "file-upload"}>
          <input
            accept={accept}
            type="file"
            id={name || "file-upload"}
            hidden
            multiple={multiple}
            onChange={handleFileChange}
          />
          <Box
            sx={{
              borderRadius: "12px",
              color: "var(--primary-900)",
              border: "1px solid var(--primary-400)",
              padding: "10px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              width: "fit-content",
              margin: layout === "row" ? 0 : "0 auto",
              backgroundColor: "var(--primary-100-light)",
              cursor: "pointer",
            }}
          >
            <Icons.ImageUserPlus
              height={20}
              width={20}
              color="var(--primary-900)"
            />
            <Typography variant="body2" sx={{ font: "var(--semibold-sm)" }}>
              {defaultBrowseLabel}
            </Typography>
          </Box>
        </label>
      </Box>
      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
    </Stack>
  );
};

export default ImageUpload;
